import { useCallback, useEffect, useMemo, useState } from "react";
import { getOtherEditors } from "../roundabout-utilities";

import type { RoundaboutCurrentRole, RoundaboutRoomUser } from "../roundabout-markings";

export interface UseAllEditorsGoneProps {
  roomUsers: RoundaboutRoomUser[];
  myRole: RoundaboutCurrentRole;
  mySub: string;
}

export const useAllEditorsGone = ({
  roomUsers,
  myRole,
  mySub,
}: UseAllEditorsGoneProps): {
  allEditorsGone: boolean;
  onCloseAllEditorsGone: () => void;
} => {
  const [allEditorsGone, setAllEditorsGone] = useState(false);

  const onCloseAllEditorsGone = useCallback(() => {
    setAllEditorsGone(false);
  }, []);

  const otherEditors = useMemo(() => getOtherEditors(roomUsers, mySub), [roomUsers, mySub]);

  // If there are no other editors, and I am viewing,
  //  Then the session at the top of the list becomes the editor.
  useEffect(() => {
    // If there are other editors or I am already editing, then do nothing
    if (otherEditors.length || myRole === "Editing") {
      return;
    }
    // This only happens when there are no other editors at all
    // which means roomUsers is all viewers plus me.

    // Apply some known order to them so we can do painful distributed logic.
    // If none or 1 user then that's fine.
    roomUsers.sort((userA, userB) =>
      userB.sessionLastSeenAt.localeCompare(userA.sessionLastSeenAt),
    );

    const firstUser = roomUsers[0];
    if (!firstUser) {
      return;
    }

    // If I am the first user, then make me the new editor
    // Theoretically only one user across all the viewers will be the editor, hopefully.
    if (firstUser.userId === mySub && firstUser.role === "Viewing") {
      // This will trigger a sync so everyone else will be updated that I am the editor

      setAllEditorsGone(true);
    }
  }, [otherEditors, roomUsers, myRole, mySub]);

  return {
    allEditorsGone,
    onCloseAllEditorsGone,
  };
};
